import { useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/client/components/ui/card";
import { Button } from "@/client/components/ui/button";
import { api } from "@/client/lib/api";
import { useAuth } from "@/client/lib/auth";
import { MailCheckIcon } from "lucide-react";

/**
 * Magic-link interstitial. The email link points here (/inloggen?token=...), NOT at a consuming GET
 * endpoint, so e-mail security scanners that pre-fetch the URL never burn the one-time token. The
 * token is consumed ONLY when the user CLICKS "Inloggen" (an explicit POST). Scanners do a GET and
 * never click, so even a scanner that runs JavaScript cannot consume it. Same Card design as LoginPage.
 */

const ERROR_COPY: Record<string, string> = {
  consumed:
    "Deze inloglink is al gebruikt. Vraag hieronder een nieuwe inloglink aan; die blijft 15 minuten geldig.",
  expired:
    "Deze inloglink is verlopen. Een link is 15 minuten geldig. Vraag hieronder een nieuwe inloglink aan.",
  invalid:
    "Deze inloglink is ongeldig. Vraag hieronder een nieuwe inloglink aan.",
  missing_token:
    "Deze inloglink is ongeldig of onvolledig. Vraag hieronder een nieuwe inloglink aan.",
  device_mismatch:
    "Deze inloglink kan in deze browser niet gebruikt worden. Voor de veiligheid van KUVO-accounts moet je de link openen in dezelfde browser waarin je hem hebt aangevraagd. Vraag hieronder een nieuwe link aan vanuit deze browser; die werkt dan meteen.",
  rate_limited:
    "Te veel pogingen achter elkaar. Wacht heel even en probeer het daarna opnieuw.",
  network:
    "We konden niet inloggen door een verbindingsprobleem. Probeer het zo nog een keer.",
};

export function InloggenPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token");
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const verify = useCallback(async () => {
    if (!token) {
      setErrorKey("missing_token");
      return;
    }
    setSubmitting(true);
    setErrorKey(null);
    try {
      const res = await api.api.auth.verify.$post({ json: { token } });
      if (res.ok) {
        // Cookie is set; load the user, then enter the portal. The root route handles the
        // onboarding / role redirect, so the user lands exactly where a verify lands today.
        await refresh();
        navigate("/", { replace: true });
        return;
      }
      let key = "invalid";
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) key = body.error;
      } catch {
        /* keep default */
      }
      setErrorKey(key);
    } catch {
      setErrorKey("network");
    } finally {
      setSubmitting(false);
    }
  }, [token, navigate, refresh]);

  // No token, or verification failed: friendly card + a clear path to a fresh link.
  if (!token || errorKey) {
    const message = ERROR_COPY[errorKey ?? "missing_token"] ?? ERROR_COPY.invalid;
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle>Inloggen niet gelukt</CardTitle>
            <CardDescription>{message}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/login", { replace: true })}>
              Vraag een nieuwe inloglink aan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Explicit-click step: the token is consumed only on this button press, never automatically.
  // Framed as a success stage ("your link works, one click left") so it does not feel like
  // starting the login over.
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-1 flex size-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <MailCheckIcon className="size-6" />
          </div>
          <CardTitle>Uw inloglink is geldig</CardTitle>
          <CardDescription>Nog één klik en u bent ingelogd bij Mijn KUVO Woerden.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={verify} disabled={submitting}>
            {submitting ? "Bezig met inloggen..." : "Doorgaan naar Mijn KUVO Woerden"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
